/**
 * 科目API函数
 * 提供获取科目列表、科目详情、题型配置等操作
 */
import { supabase } from '../supabaseClient';

/**
 * 获取所有科目列表
 * @param {object} options - 查询选项
 * @returns {Promise<Array>} 科目列表
 */
export const getAllSubjects = async (options = {}) => {
  try {
    const { limit, orderBy = '_id', ascending = false } = options;
    
    let query = supabase
      .from('subjects')
      .select('*')
      .order(orderBy, { ascending });
      
    if (limit) {
      query = query.limit(limit);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('获取科目列表出错:', error);
    throw error;
  }
};

/**
 * 获取科目详情
 * @param {number} subjectId - 科目ID
 * @returns {Promise<Object>} 科目详情
 */
export const getSubjectById = async (subjectId) => {
  try {
    const { data, error } = await supabase
      .from('subjects')
      .select('*')
      .eq('_id', Number(subjectId))
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('获取科目详情出错:', error);
    throw error;
  }
};

/**
 * 根据考试ID获取科目列表
 * @param {number} examId - 考试ID
 * @returns {Promise<Array>} 科目列表
 */
export const getSubjectsByExamId = async (examId) => {
  try {
    const { data, error } = await supabase
      .from('subjects')
      .select('*')
      .eq('pid', Number(examId));
      
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('获取考试科目出错:', error);
    throw error;
  }
};

/**
 * 获取科目的题型配置
 * @param {number} subjectId - 科目ID
 * @returns {Promise<Array>} 题型配置列表
 */
export const getSubjectQuestionConfig = async (subjectId) => {
  try {
    const { data, error } = await supabase
      .from('subject_question')
      .select('*')
      .eq('subject_id', Number(subjectId))
      .order('id', { ascending: true });
      
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('获取科目题型配置出错:', error);
    throw error;
  }
};

/**
 * 解析题型数量配置
 * @param {string|object} typeCountStr - 题型数量配置字符串或对象
 * @returns {object|null} 解析后的题型数量对象或null
 */
export const parseTypeCount = (typeCountStr) => {
  try {
    if (!typeCountStr) return null;
    const typeCount = typeof typeCountStr === 'string' 
      ? JSON.parse(typeCountStr) 
      : typeCountStr;
    return typeCount;
  } catch (error) {
    console.error('题型配置解析错误:', error);
    return null;
  }
};

/**
 * 计算科目总题目数
 * @param {number|object} subject - 科目ID或科目对象
 * @returns {Promise<number|null>} 题目总数或null
 */
export const calculateSubjectTotalQuestions = async (subject) => {
  try {
    let subjectData = subject;
    
    // 如果传入的是ID，先获取科目信息
    if (typeof subject === 'number') {
      subjectData = await getSubjectById(subject);
      if (!subjectData) {
        throw new Error('科目不存在');
      }
    }
    
    // 首先检查subject_question配置
    const subjectQuestions = await getSubjectQuestionConfig(subjectData._id);
      
    if (subjectQuestions && subjectQuestions.length > 0) {
      // 使用subject_question表的配置计算题目数
      return subjectQuestions.reduce((sum, sq) => sum + sq.question_count, 0);
    } else if (subjectData.type_count) {
      // 尝试使用type_count字段
      try {
        const typeCount = typeof subjectData.type_count === 'string' 
          ? JSON.parse(subjectData.type_count) 
          : subjectData.type_count;
        return Object.values(typeCount).reduce((sum, count) => sum + count, 0);
      } catch (e) {
        console.error('解析type_count出错:', e);
        if (subjectData.count) {
          return subjectData.count;
        }
      }
    } else if (subjectData.count) {
      // 使用科目自己的count字段
      return subjectData.count;
    }
    
    return null;
  } catch (error) {
    console.error('计算科目题目总数出错:', error);
    return null;
  }
};

/**
 * 创建新科目
 * @param {object} subjectData - 科目数据
 * @returns {Promise<object>} 创建的科目数据
 */
export const createSubject = async (subjectData) => {
  try {
    const { data, error } = await supabase
      .from('subjects')
      .insert([subjectData])
      .select();
      
    if (error) throw error;
    
    return data[0];
  } catch (error) {
    console.error('创建科目出错:', error);
    throw error;
  }
};

/**
 * 更新科目信息
 * @param {number} subjectId - 科目ID
 * @param {object} updates - 更新的数据
 * @returns {Promise<object>} 更新后的科目数据
 */
export const updateSubject = async (subjectId, updates) => {
  try {
    const { data, error } = await supabase
      .from('subjects')
      .update(updates)
      .eq('_id', Number(subjectId))
      .select();
      
    if (error) throw error;
    
    return data[0];
  } catch (error) {
    console.error('更新科目出错:', error);
    throw error;
  }
};

/**
 * 删除科目
 * @param {number} subjectId - 科目ID
 * @returns {Promise<void>}
 */
export const deleteSubject = async (subjectId) => {
  try {
    const { error } = await supabase
      .from('subjects')
      .delete()
      .eq('_id', Number(subjectId));
      
    if (error) throw error;
  } catch (error) {
    console.error('删除科目出错:', error);
    throw error;
  }
}; 